import { Component, HostListener, OnDestroy, OnInit, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToastService } from '../../../services/toast-service';
import { AppSettings } from '../../../shared/app-settings';
import { QuickCaptureApiService } from '../quick-capture-api.service';
import {
  QuickCapturePriority,
  QuickCaptureProjectOption,
  QuickCaptureRequest,
  QuickCaptureUserOption,
} from '../quick-capture.models';

const RECENT_PROJECTS_KEY = 'quickCapture.recentProjectIds';
const RECENT_PROJECTS_MAX = 5;

@Component({
  selector: 'app-quick-capture-drawer',
  templateUrl: './quick-capture-drawer.component.html',
  styleUrls: ['./quick-capture-drawer.component.scss']
})
export class QuickCaptureDrawerComponent implements OnInit, OnDestroy {
  @ViewChild('titleInput') titleInput?: ElementRef<HTMLInputElement>;

  form: FormGroup;
  users: QuickCaptureUserOption[] = [];
  projects: QuickCaptureProjectOption[] = [];
  /**
   * Stable, precomputed picker list (recent first, then alphabetical) with a `groupLabel`
   * for ng-select [groupBy]. MUST be a plain field — a getter here returned a new array with
   * new object references on every change-detection pass, which made ng-select (virtualScroll +
   * groupBy) re-process its items endlessly and froze the browser when the dropdown opened.
   */
  projectsForPicker: (QuickCaptureProjectOption & { groupLabel: string })[] = [];
  recentProjectIds: number[] = [];
  loading = true;
  saving = false;

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private api: QuickCaptureApiService,
    private toast: ToastService,
    private translate: TranslateService,
    public activeOffcanvas: NgbActiveOffcanvas,
  ) {
    this.form = this.fb.group({
      taskTitle: ['', [Validators.required, Validators.maxLength(500)]],
      assignedToUserId: [this.getCurrentUserId(), [Validators.required]],
      projectId: [null],
      taskDescription: [''],
      taskDueDate: [null],
      taskPriority: ['Medium' as QuickCapturePriority],
    });
  }

  ngOnInit(): void {
    this.recentProjectIds = this.loadRecentProjectIds();

    forkJoin({
      users: this.api.getAssignableUsers(),
      projects: this.api.getAccessibleProjects(),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ users, projects }) => {
          if (users?.success) this.users = users.result ?? [];
          if (projects?.success) this.projects = projects.result ?? [];
          this.rebuildProjectsForPicker();
          this.loading = false;
          // Re-focus the title once the drawer's layout settles after loading.
          setTimeout(() => this.titleInput?.nativeElement?.focus(), 0);
        },
        error: () => {
          this.loading = false;
          this.toast.show({
            type: 'error',
            data: [this.translate.instant('QuickCapture.errorLoad')],
          });
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Rebuilds {@link projectsForPicker} once: recent projects first, then the rest
   * alphabetically, each tagged with a `groupLabel` for ng-select [groupBy]. Call this only
   * when the inputs change (projects loaded, recents updated) — never from a template binding.
   */
  private rebuildProjectsForPicker(): void {
    if (this.projects.length === 0) {
      this.projectsForPicker = [];
      return;
    }

    const recentLabel = this.translate.instant('QuickCapture.recentProjects');
    const restLabel = this.translate.instant('QuickCapture.allProjects');
    const recentSet = new Set(this.recentProjectIds);

    const recent = this.recentProjectIds
      .map((id) => this.projects.find((p) => p.projectId === id))
      .filter((p): p is QuickCaptureProjectOption => !!p)
      .map((p) => ({ ...p, groupLabel: recentLabel }));

    const rest = this.projects
      .filter((p) => !recentSet.has(p.projectId))
      .sort((a, b) => a.projectTitle.localeCompare(b.projectTitle, 'nb'))
      .map((p) => ({ ...p, groupLabel: restLabel }));

    this.projectsForPicker = [...recent, ...rest];
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
      event.preventDefault();
      this.save();
    }
  }

  save(): void {
    if (this.form.invalid || this.saving) {
      this.form.markAllAsTouched();
      return;
    }

    const v = this.form.value;
    const request: QuickCaptureRequest = {
      taskTitle: (v.taskTitle as string).trim(),
      assignedToUserId: v.assignedToUserId,
      projectId: v.projectId ?? undefined,
      taskDescription: v.taskDescription?.trim() || undefined,
      taskDueDate: v.taskDueDate ? new Date(v.taskDueDate).toISOString() : undefined,
      taskPriority: v.taskPriority,
    };

    this.saving = true;
    this.api.capture(request).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        this.saving = false;
        if (!response?.success) {
          this.toast.showApiError(
            response?.errorMessage,
            this.translate.instant('QuickCapture.errorSave'),
          );
          return;
        }

        if (request.projectId) {
          this.pushRecentProjectId(request.projectId);
        }

        this.toast.show({
          type: 'success',
          data: [this.translate.instant('QuickCapture.successCreated')],
        });

        this.activeOffcanvas.close();
      },
      error: () => {
        this.saving = false;
        this.toast.show({
          type: 'error',
          data: [this.translate.instant('QuickCapture.errorSave')],
        });
      },
    });
  }

  cancel(): void {
    this.activeOffcanvas.dismiss();
  }

  private getCurrentUserId(): number | null {
    try {
      const raw = localStorage.getItem(AppSettings.StorageKeys.User);
      if (!raw) return null;
      const user = JSON.parse(raw);
      return typeof user?.userId === 'number' ? user.userId : null;
    } catch {
      return null;
    }
  }

  private loadRecentProjectIds(): number[] {
    try {
      const raw = localStorage.getItem(RECENT_PROJECTS_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'number').slice(0, RECENT_PROJECTS_MAX) : [];
    } catch {
      return [];
    }
  }

  private pushRecentProjectId(projectId: number): void {
    const next = [projectId, ...this.recentProjectIds.filter((id) => id !== projectId)].slice(0, RECENT_PROJECTS_MAX);
    try {
      localStorage.setItem(RECENT_PROJECTS_KEY, JSON.stringify(next));
    } catch {
      // Ignore quota errors silently — recents is a UX nicety, not load-bearing.
    }
    this.recentProjectIds = next;
  }
}
